/**
 * Etecplus behaviours: hero slideshow, countdown, tabs, product shelves and the desktop category bar.
 * Loaded with `defer` from layout/theme.liquid. No dependencies.
 */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const inEditor = () => Boolean(window.Shopify && window.Shopify.designMode);

  /* ---------- Hero slideshow ------------------------------------------------ */

  class EtecHeroSlider extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-etec-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-etec-dot]'));
      this.progress = this.querySelector('[data-etec-progress]');
      this.currentLabel = this.querySelector('[data-etec-current]');
      this.speed = (parseInt(this.dataset.speed, 10) || 6) * 1000;
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1 && !reduceMotion.matches;
      this.index = Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains('is-active'))
      );
      this.held = false;

      this.dots.forEach((dot, i) =>
        dot.addEventListener('click', () => {
          this.show(i);
          this.restart();
        })
      );

      this.addEventListener('mouseenter', () => this.hold(true));
      this.addEventListener('mouseleave', () => this.hold(false));
      this.addEventListener('focusin', () => this.hold(true));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.hold(false);
      });

      this.addEventListener(
        'touchstart',
        (event) => {
          this.touchX = event.changedTouches[0].clientX;
        },
        { passive: true }
      );
      this.addEventListener(
        'touchend',
        (event) => {
          if (this.touchX == null) return;
          const delta = event.changedTouches[0].clientX - this.touchX;
          this.touchX = null;
          if (Math.abs(delta) < 50) return;
          this.show(this.index + (delta < 0 ? 1 : -1));
          this.restart();
        },
        { passive: true }
      );

      this.onVisibility = () => (document.hidden ? this.stop() : this.start());
      document.addEventListener('visibilitychange', this.onVisibility);

      // Theme editor: show the slide being edited and keep it still.
      this.addEventListener('shopify:block:select', (event) => {
        const i = this.slides.indexOf(event.target);
        if (i < 0) return;
        this.editing = true;
        this.stop();
        this.show(i);
      });
      this.addEventListener('shopify:block:deselect', () => {
        this.editing = false;
        this.start();
      });

      this.show(this.index);
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    show(i) {
      const count = this.slides.length;
      if (!count) return;
      this.index = ((i % count) + count) % count;

      this.slides.forEach((slide, n) => slide.classList.toggle('is-active', n === this.index));
      this.dots.forEach((dot, n) => {
        dot.classList.toggle('is-active', n === this.index);
        if (n === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (this.currentLabel) this.currentLabel.textContent = String(this.index + 1).padStart(2, '0');
    }

    hold(state) {
      this.held = state;
      if (state) this.stop();
      else this.start();
    }

    start() {
      if (!this.autoplay || this.held || this.editing || this.timer) return;
      this.runProgress();
      this.timer = window.setInterval(() => {
        this.show(this.index + 1);
        this.runProgress();
      }, this.speed);
    }

    stop() {
      window.clearInterval(this.timer);
      this.timer = null;
      if (this.progress) this.progress.classList.remove('is-running');
    }

    restart() {
      this.stop();
      this.start();
    }

    runProgress() {
      if (!this.progress) return;
      this.progress.classList.remove('is-running');
      void this.progress.offsetWidth;
      this.progress.classList.add('is-running');
    }
  }

  /* ---------- Countdown ------------------------------------------------------ */

  class EtecCountdown extends HTMLElement {
    connectedCallback() {
      this.parts = {
        days: this.querySelector('[data-etec-days]'),
        hours: this.querySelector('[data-etec-hours]'),
        minutes: this.querySelector('[data-etec-minutes]'),
        seconds: this.querySelector('[data-etec-seconds]'),
      };
      this.end = this.getEnd();
      if (!this.end) return;
      this.tick();
      this.timer = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    getEnd() {
      if (this.dataset.mode === 'week') {
        const now = new Date();
        const end = new Date(now);
        end.setDate(now.getDate() + ((7 - now.getDay()) % 7));
        end.setHours(23, 59, 59, 0);
        return end;
      }
      // Unix seconds from Liquid: the same moment for every shopper, whatever their timezone.
      const epoch = parseInt(this.dataset.endEpoch, 10);
      if (epoch > 0) return new Date(epoch * 1000);
      const raw = (this.dataset.end || '').trim();
      if (!raw) return null;
      const date = new Date(raw.replace(' ', 'T'));
      return Number.isNaN(date.getTime()) ? null : date;
    }

    tick() {
      let seconds = Math.floor((this.end.getTime() - Date.now()) / 1000);
      // The last day reads as urgent (styled in CSS).
      this.classList.toggle('is-urgent', seconds > 0 && seconds < 86400);

      if (seconds <= 0) {
        if (this.dataset.mode === 'week') {
          this.end = this.getEnd();
          seconds = Math.max(0, Math.floor((this.end.getTime() - Date.now()) / 1000));
        } else {
          this.hidden = true;
          window.clearInterval(this.timer);
          this.dataset.ended = 'true';
          this.dispatchEvent(new CustomEvent('etec:countdown-end', { bubbles: true }));
          return;
        }
      }

      const pad = (value) => String(value).padStart(2, '0');
      const days = Math.floor(seconds / 86400);
      seconds -= days * 86400;
      const hours = Math.floor(seconds / 3600);
      seconds -= hours * 3600;
      const minutes = Math.floor(seconds / 60);
      seconds -= minutes * 60;

      if (this.parts.days) this.parts.days.textContent = pad(days);
      if (this.parts.hours) this.parts.hours.textContent = pad(hours);
      if (this.parts.minutes) this.parts.minutes.textContent = pad(minutes);
      if (this.parts.seconds && this.parts.seconds.textContent !== pad(seconds)) {
        this.parts.seconds.textContent = pad(seconds);
        // Alternating classes swap the CSS animation each second, so the digit can drop in every tick.
        this.parts.seconds.classList.toggle('is-tick');
      }
      this.hidden = false;
    }
  }

  /* ---------- Cart helper ------------------------------------------------------ */

  // Shared by the bundle cards and the PC builder: which of Dawn's cart sections to ask for with a cart
  // request, and re-rendering the drawer (or notification) from the response. `render` returns false when
  // it can't, so the caller can fall back to the cart page.
  const cartRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  window.EtecCart = {
    url: () => (window.routes && window.routes.cart_url) || `${cartRoot()}cart`,
    element: () => document.querySelector('cart-notification') || document.querySelector('cart-drawer'),
    sections() {
      const cart = this.element();
      if (!cart || typeof cart.getSectionsToRender !== 'function' || typeof cart.renderContents !== 'function') {
        return null;
      }
      return { sections: cart.getSectionsToRender().map((section) => section.id), sections_url: window.location.pathname };
    },
    render(data, trigger) {
      const cart = this.element();
      if (!cart || !data || !data.sections || typeof cart.renderContents !== 'function') return false;
      try {
        const lines = Array.isArray(data.items) ? data.items : [];
        const last = lines[lines.length - 1];
        // Dawn expects the single-item response shape: the line key and product id at the top level.
        const state = Object.assign({}, data);
        if (last && state.key == null) state.key = last.key;
        if (last && state.id == null) state.id = last.id;
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(trigger || document.activeElement);
        cart.renderContents(state);
        cart.classList.remove('is-empty');
        return true;
      } catch (error) {
        return false;
      }
    },
    async request(path, body) {
      const response = await fetch(`${cartRoot()}${path}`, {
        method: body ? 'POST' : 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) {
        const detail = data.description || data.message;
        throw new Error(typeof detail === 'string' ? detail : '');
      }
      return data;
    },
    // Adds a discount code to the cart, keeping any codes already there (Shopify replaces the list with
    // what is sent). Returns the cart response and whether Shopify says the code applies.
    async applyCode(code, sections) {
      const cart = await this.request('cart.js');
      const existing = (cart.discount_codes || []).map((entry) => entry.code).filter(Boolean);
      const codes = existing.some((entry) => entry.toLowerCase() === code.toLowerCase()) ? existing : existing.concat(code);
      const data = await this.request('cart/update.js', Object.assign({ discount: codes.join(',') }, sections || {}));
      const match = (data.discount_codes || []).find((entry) => entry.code.toLowerCase() === code.toLowerCase());
      return { data, applied: Boolean(match && match.applicable !== false) };
    },
  };

  /* ---------- Bundle deals ------------------------------------------------------ */

  // A bundle card (sections/etec-bundles.liquid). The button adds every item, then applies the bundle's
  // discount code alongside any code already on the cart, and opens the cart drawer. When the card's
  // countdown runs out it locks, so a page left open can't add an expired deal.
  class EtecBundle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-etec-bundle-add]');
      this.message = this.querySelector('[data-etec-bundle-message]');
      this.addEventListener('etec:countdown-end', () => this.end());
      const countdown = this.querySelector('etec-countdown');
      if (countdown && countdown.dataset.ended === 'true') this.end();
      // With one timer for the whole section, its countdown sits in the heading and ends every card.
      const section = this.closest('[data-etec-bundles]');
      const saleTimer = section && section.querySelector('.etec-countdown--sale');
      if (saleTimer) {
        if (saleTimer.dataset.ended === 'true') this.end();
        saleTimer.addEventListener('etec:countdown-end', () => this.end());
      }
      if (this.button) this.button.addEventListener('click', () => this.add());
    }

    end() {
      this.classList.add('is-ended');
      if (this.button) this.button.disabled = true;
      this.say(this.dataset.endedText || 'This deal has ended.', 'info');
    }

    say(text, tone) {
      if (!this.message) return;
      this.message.textContent = text || '';
      this.message.dataset.tone = tone || '';
      this.message.hidden = !text;
    }

    async add() {
      if (!this.button || this.button.disabled || this.classList.contains('is-ended')) return;
      let items = [];
      try {
        items = JSON.parse(this.dataset.items || '[]');
      } catch (error) {
        items = [];
      }
      if (!items.length) return;

      const code = (this.dataset.code || '').trim();
      const sections = window.EtecCart.sections();
      this.button.disabled = true;
      this.classList.add('is-busy');
      this.say('');

      try {
        let data = await window.EtecCart.request('cart/add.js', code || !sections ? { items } : Object.assign({ items }, sections));
        if (code) {
          const result = await window.EtecCart.applyCode(code, sections);
          data = result.data;
          if (!result.applied) {
            // Discounts don't stack: when the cart already has the same or a bigger saving (a bulk buy tier or
            // another code), Shopify keeps that one and leaves this code unused, which is not an error.
            const better = Number(data && data.total_discount) > 0;
            if (better) {
              this.say(this.dataset.codeBetter || "Bundle added. Your cart already has the same or a bigger saving, so that one is applied.", 'info');
            } else {
              this.say(this.dataset.codeError || "Items added. The bundle discount couldn't be applied, so please contact us.", 'error');
            }
          }
        }
        if (!window.EtecCart.render(data, this.button)) {
          window.location.href = window.EtecCart.url();
          return;
        }
        // Confirm on the button itself for a moment, then return to the normal label.
        this.classList.add('is-added');
        window.clearTimeout(this.addedTimer);
        this.addedTimer = window.setTimeout(() => this.classList.remove('is-added'), 2200);
      } catch (error) {
        this.say(error.message || this.dataset.cartError || "Something went wrong adding this bundle. Please try again.", 'error');
      } finally {
        this.classList.remove('is-busy');
        if (!this.classList.contains('is-ended')) this.button.disabled = false;
      }
    }
  }

  if (!customElements.get('etec-bundle')) customElements.define('etec-bundle', EtecBundle);

  /* ---------- Tabs ------------------------------------------------------------ */

  class EtecTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      if (!this.tabs.length) return;
      this.panels = this.tabs.map((tab) => this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`));

      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (event) => {
          const last = this.tabs.length - 1;
          let next = null;
          if (event.key === 'ArrowRight') next = i === last ? 0 : i + 1;
          if (event.key === 'ArrowLeft') next = i === 0 ? last : i - 1;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = last;
          if (next === null) return;
          event.preventDefault();
          this.select(next);
          this.tabs[next].focus();
        });
      });

      // Theme editor: open the tab whose block is selected.
      this.addEventListener('shopify:block:select', (event) => {
        const i = Math.max(this.tabs.indexOf(event.target), this.panels.indexOf(event.target));
        if (i >= 0) this.select(i);
      });
    }

    select(i) {
      this.tabs.forEach((tab, n) => {
        const active = n === i;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active) tab.removeAttribute('tabindex');
        else tab.setAttribute('tabindex', '-1');
        if (this.panels[n]) this.panels[n].hidden = !active;
      });
    }
  }

  /* ---------- Shelf ------------------------------------------------------------- */

  // A row of product cards that scrolls sideways. The arrows move it by the cards that fit, and switch
  // off at each end. Touch screens hide the arrows (see assets/etec.css) and scroll by swiping.
  class EtecShelf extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-etec-shelf-track]');
      this.prev = this.querySelector('[data-etec-shelf-prev]');
      this.next = this.querySelector('[data-etec-shelf-next]');
      if (!this.track || !this.prev || !this.next) return;

      this.prev.addEventListener('click', () => this.page(-1));
      this.next.addEventListener('click', () => this.page(1));

      this.queueUpdate = () => {
        if (this.frame) return;
        this.frame = window.requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };
      this.track.addEventListener('scroll', this.queueUpdate, { passive: true });
      // Also covers a shelf that starts inside a hidden tab and gets its size when the tab opens.
      if ('ResizeObserver' in window) {
        this.sizes = new ResizeObserver(this.queueUpdate);
        this.sizes.observe(this.track);
      }
      this.update();
    }

    disconnectedCallback() {
      if (this.sizes) this.sizes.disconnect();
    }

    page(direction) {
      const cards = this.track.children;
      if (!cards.length) return;
      // Distance from one card to the next, so a page always starts on a whole card.
      const step = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : cards[0].offsetWidth;
      const fit = Math.max(1, Math.round(this.track.clientWidth / step - 0.3));
      this.track.scrollBy({ left: direction * fit * step, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }

    update() {
      const end = this.track.scrollWidth - this.track.clientWidth;
      const at = this.track.scrollLeft;
      this.prev.setAttribute('aria-disabled', at <= 1 ? 'true' : 'false');
      this.next.setAttribute('aria-disabled', at >= end - 1 ? 'true' : 'false');
    }
  }

  if (!customElements.get('etec-hero-slider')) customElements.define('etec-hero-slider', EtecHeroSlider);
  if (!customElements.get('etec-shelf')) customElements.define('etec-shelf', EtecShelf);
  if (!customElements.get('etec-countdown')) customElements.define('etec-countdown', EtecCountdown);
  if (!customElements.get('etec-tabs')) customElements.define('etec-tabs', EtecTabs);

  /* ---------- Welcome popup ------------------------------------------------------ */
  // Sign-up popup (sections/etec-welcome-popup.liquid). It opens once, after the section's delay or when the
  // pointer leaves the top of the window on a computer, unless the visitor closed it in the last few days or
  // has already signed up. The form posts normally so Shopify's spam check keeps working. A note in
  // sessionStorage tells the reloaded page that the post came from this popup, so it reopens on the error.
  // A successful sign-up from any newsletter form reopens it on the discount code.

  const WELCOME_KEY = 'etec-welcome';
  const WELCOME_POST_TTL = 15 * 60 * 1000;
  const storageGet = (store, key) => {
    try {
      return window[store].getItem(key);
    } catch (error) {
      return null;
    }
  };
  const storageSet = (store, key, value) => {
    try {
      if (value == null) window[store].removeItem(key);
      else window[store].setItem(key, value);
    } catch (error) {
      /* Storage is blocked: the popup just won't remember. */
    }
  };

  class EtecWelcomePopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.sectionId = this.dataset.sectionId;
      this.status = this.dataset.status || '';

      this.querySelectorAll('[data-etec-welcome-close]').forEach((button) =>
        button.addEventListener('click', () => this.close())
      );
      // A click on the dialog itself, outside its panel, is a click on the backdrop.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.dialog.addEventListener('close', () => this.onClose());

      const form = this.querySelector('.etec-welcome__form');
      if (form) {
        form.addEventListener('submit', () => storageSet('sessionStorage', `${WELCOME_KEY}:posted`, String(Date.now())));
      }

      const copy = this.querySelector('[data-etec-welcome-copy]');
      if (copy) copy.addEventListener('click', () => this.copy(copy));

      if (inEditor()) {
        this.bindEditor();
        return;
      }

      const postedAt = parseInt(storageGet('sessionStorage', `${WELCOME_KEY}:posted`), 10) || 0;
      const postedHere = Date.now() - postedAt < WELCOME_POST_TTL;

      if (this.status === 'success') {
        storageSet('sessionStorage', `${WELCOME_KEY}:posted`, null);
        storageSet('localStorage', `${WELCOME_KEY}:subscribed`, '1');
        this.clearPostedParam();
        this.open('success');
        return;
      }
      if (this.status === 'error' && postedHere) {
        storageSet('sessionStorage', `${WELCOME_KEY}:posted`, null);
        this.open('form');
        return;
      }
      // Part way through a sign-up (for example on Shopify's spam check page): stay out of the way.
      if (postedHere) return;
      if (this.canAutoOpen()) this.arm();
    }

    disconnectedCallback() {
      this.disarm();
      clearTimeout(this.copyTimer);
      if (this.onEditorSelect) {
        document.removeEventListener('shopify:section:select', this.onEditorSelect);
        document.removeEventListener('shopify:section:deselect', this.onEditorDeselect);
      }
    }

    canAutoOpen() {
      if (storageGet('localStorage', `${WELCOME_KEY}:subscribed`) === '1') return false;
      const until = parseInt(storageGet('localStorage', `${WELCOME_KEY}:dismissed-until`), 10) || 0;
      if (until > Date.now()) return false;
      if (this.dataset.mobile === 'false' && window.matchMedia('(max-width: 749px)').matches) return false;
      return true;
    }

    arm() {
      const delay = Math.max(0, parseInt(this.dataset.delay, 10) || 0) * 1000;
      this.timer = setTimeout(() => this.tryOpen(), delay);
      // Exit intent on computers: the pointer leaves through the top of the window, towards the tabs.
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        const armedAt = Date.now();
        this.onExit = (event) => {
          if (event.relatedTarget || event.clientY > 0 || Date.now() - armedAt < 2000) return;
          this.tryOpen();
        };
        document.addEventListener('mouseout', this.onExit);
      }
    }

    disarm() {
      clearTimeout(this.timer);
      clearTimeout(this.retry);
      if (this.onExit) document.removeEventListener('mouseout', this.onExit);
      this.onExit = null;
    }

    // Don't cover another dialog, the cart drawer or a menu, or interrupt typing. Try again a little later.
    busy() {
      if (/(^|\s)overflow-hidden/.test(document.body.className)) return true;
      if (document.querySelector('dialog[open], cart-drawer.active, details[open] > .menu-drawer')) return true;
      const active = document.activeElement;
      return Boolean(active && active.matches && active.matches('input, textarea, select, [contenteditable="true"]'));
    }

    tryOpen() {
      if (this.dialog.open) return;
      clearTimeout(this.retry);
      if (this.busy()) {
        this.retry = setTimeout(() => this.tryOpen(), 4000);
        return;
      }
      this.disarm();
      this.open('form');
    }

    open(view) {
      this.view = view === 'success' ? 'success' : 'form';
      this.querySelectorAll('[data-etec-welcome-view]').forEach((panel) => {
        panel.hidden = panel.dataset.etecWelcomeView !== this.view;
      });
      if (this.dialog.open) return;
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
    }

    close() {
      if (!this.dialog.open) return;
      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.onClose();
      }
    }

    // Closing the sign-up form snoozes the popup for the section's number of days.
    onClose() {
      if (inEditor() || this.view !== 'form') return;
      const days = Math.max(1, parseInt(this.dataset.days, 10) || 7);
      storageSet('localStorage', `${WELCOME_KEY}:dismissed-until`, String(Date.now() + days * 86400000));
    }

    async copy(button) {
      const code = button.dataset.code || '';
      const status = this.querySelector('[data-etec-welcome-copy-status]');
      let copied = false;
      try {
        await navigator.clipboard.writeText(code);
        copied = true;
      } catch (error) {
        // No clipboard access: select the code so it can be copied by hand.
        const value = this.querySelector('[data-etec-welcome-code]');
        const selection = window.getSelection();
        if (value && selection) {
          const range = document.createRange();
          range.selectNodeContents(value);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
      button.textContent = copied ? 'Copied!' : 'Code selected';
      if (status) status.textContent = copied ? `${code} copied` : `${code} selected, copy it to use at checkout`;
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => {
        button.textContent = 'Copy code';
      }, 2400);
    }

    // After a sign-up Shopify adds ?customer_posted=true to the address. Drop it so a refresh doesn't
    // open the popup again.
    clearPostedParam() {
      try {
        const url = new URL(window.location.href);
        if (!url.searchParams.has('customer_posted')) return;
        url.searchParams.delete('customer_posted');
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (error) {
        /* Leave the address as it is. */
      }
    }

    // Theme editor: it never opens by itself. Selecting the section opens it on the chosen preview.
    bindEditor() {
      this.onEditorSelect = (event) => {
        if (!event.detail || event.detail.sectionId !== this.sectionId) return;
        window.etecWelcomeEditing = this.sectionId;
        this.open(this.dataset.preview);
      };
      this.onEditorDeselect = (event) => {
        if (!event.detail || event.detail.sectionId !== this.sectionId) return;
        window.etecWelcomeEditing = null;
        this.close();
      };
      document.addEventListener('shopify:section:select', this.onEditorSelect);
      document.addEventListener('shopify:section:deselect', this.onEditorDeselect);
      // Changing a setting reloads the section, so reopen it if it was being edited.
      if (window.etecWelcomeEditing === this.sectionId) this.open(this.dataset.preview);
    }
  }

  if (!customElements.get('etec-welcome-popup')) customElements.define('etec-welcome-popup', EtecWelcomePopup);

  /* ---------- Free delivery progress bar ---------------------------------------- */
  // The bar markup comes from snippets/etec-shipping-bar.liquid; the threshold and messages
  // come from the "Etecplus brand" section (JSON in #EtecConfig).

  const readConfig = () => {
    const node = document.getElementById('EtecConfig');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  };

  const formatMoney = (cents, format) => {
    const withDelimiters = (number, precision, thousands, decimal) => {
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? whole + decimal + parts[1] : whole;
    };
    const pattern = /\{\{\s*(\w+)\s*\}\}/;
    const template = format && pattern.test(format) ? format : '${{amount}}';
    const style = template.match(pattern)[1];
    let value;
    switch (style) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }
    // Drop ".00" so "Add $26 more" reads cleanly.
    return template.replace(pattern, value.replace(/([.,])00$/, ''));
  };

  const updateShippingBars = (force) => {
    const config = readConfig();
    const threshold = config ? Number(config.freeShippingThreshold) || 0 : 0;

    document.querySelectorAll('[data-etec-ship]').forEach((bar) => {
      const total = parseInt(bar.dataset.total, 10) || 0;
      const stamp = `${total}:${threshold}`;
      if (!force && bar.dataset.etecStamp === stamp) return;
      bar.dataset.etecStamp = stamp;

      if (threshold <= 0) {
        bar.hidden = true;
        return;
      }

      const remaining = threshold - total;
      const percent = Math.max(0, Math.min(100, Math.round((total / threshold) * 100)));
      const message = bar.querySelector('[data-etec-ship-msg]');
      const track = bar.querySelector('.etec-ship__track');
      const fill = bar.querySelector('.etec-ship__fill');
      const doneText = config.doneText || '';
      const text =
        remaining > 0
          ? (config.progressText || '').replace('AMOUNT', formatMoney(remaining, config.moneyFormat))
          : doneText;

      if (message) message.textContent = text;
      if (fill) fill.style.width = `${percent}%`;
      if (track) {
        track.setAttribute('aria-valuenow', String(percent));
        track.setAttribute('aria-label', text);
      }
      bar.classList.toggle('etec-ship--done', remaining <= 0);
      bar.hidden = false;
    });
  };

  let shippingFrame = null;
  const queueShippingUpdate = () => {
    if (shippingFrame) return;
    shippingFrame = window.requestAnimationFrame(() => {
      shippingFrame = null;
      updateShippingBars(false);
    });
  };

  updateShippingBars(true);
  // Dawn swaps the cart markup after every change, so watch for new bars arriving.
  new MutationObserver(queueShippingUpdate).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', () => updateShippingBars(true));

  /* ---------- Bulk buy tiers (product page) ------------------------------------ */
  // Rows and their prices come from snippets/etec-bulk-buy.liquid. Picking a row sets Dawn's quantity box, and
  // typing in the box picks the row. Tiers mirror Shopify automatic discounts that count every item in the
  // cart, so the meter and the saving use the items already in the cart plus this quantity.
  // Defined after formatMoney/readConfig: connectedCallback runs as soon as the element is defined.

  class EtecBulkBuy extends HTMLElement {
    connectedCallback() {
      // Quick add reuses the product section; the block is hidden there and left alone.
      if (this.closest('quick-add-modal')) return;
      this.input = document.getElementById(`Quantity-${this.dataset.section}`);
      if (!this.input) return;
      // The variant id the page's own form will add. Quick add modals have their own form.
      this.idInput = document.querySelector(`#product-form-${this.dataset.section} [name="id"]`);

      this.wanted = parseInt(this.input.value, 10) || 1;

      this.onPick = (event) => {
        if (!event.target.matches('.etec-bulk__input')) return;
        this.wanted = parseInt(event.target.value, 10) || 1;
        this.setQuantity(this.wanted);
        this.render();
      };
      this.onTyped = () => {
        if (this.applying) return;
        const qty = parseInt(this.input.value, 10);
        if (!qty || qty < 1) return;
        this.wanted = qty;
        this.render();
      };
      // Dawn updates the id before announcing a variant change, and empties it for an option combination
      // that doesn't exist (no variantChange follows then): drop any refresh for the old variant and hide.
      this.onVariantId = () => {
        this.cancelRefresh();
        if (!this.idInput.value) this.hidden = true;
      };
      this.addEventListener('change', this.onPick);
      this.input.addEventListener('change', this.onTyped);
      this.input.addEventListener('input', this.onTyped);
      if (this.idInput) this.idInput.addEventListener('change', this.onVariantId);

      this.unsubscribers = [];
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribers.push(
          subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.restore()),
          subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event)),
          subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event))
        );
      }

      this.render();
    }

    disconnectedCallback() {
      (this.unsubscribers || []).forEach((unsubscribe) => unsubscribe());
      this.unsubscribers = [];
      if (this.onPick) this.removeEventListener('change', this.onPick);
      if (this.input && this.onTyped) {
        this.input.removeEventListener('change', this.onTyped);
        this.input.removeEventListener('input', this.onTyped);
      }
      if (this.idInput && this.onVariantId) this.idInput.removeEventListener('change', this.onVariantId);
      this.cancelRefresh();
    }

    get tiers() {
      try {
        return JSON.parse(this.dataset.tiers || '[]');
      } catch (error) {
        return [];
      }
    }

    get cap() {
      return this.dataset.cap ? parseInt(this.dataset.cap, 10) : Infinity;
    }

    // Most that can be added: the stock cap worked out in Liquid and any maximum on the quantity box.
    allowed(qty) {
      let limit = this.cap;
      if (this.input.max) limit = Math.min(limit, parseInt(this.input.max, 10));
      return Math.max(1, Math.min(qty, limit));
    }

    setQuantity(qty) {
      const value = String(this.allowed(qty));
      if (this.input.value === value) return;
      this.applying = true;
      this.input.value = value;
      // Lets Dawn's <quantity-input> update its +/- buttons.
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
      this.applying = false;
    }

    // Dawn resets the quantity to its minimum on variant changes and cart updates (product-info.js,
    // setQuantityBoundries); put the chosen amount back.
    restore() {
      if (this.hidden) {
        this.wanted = parseInt(this.input.value, 10) || 1;
        return;
      }
      if (this.wanted > 1) this.setQuantity(this.wanted);
      this.render();
    }

    onVariantChange(event) {
      const data = event && event.data;
      if (!data || !data.html || data.sectionId !== this.dataset.section) return;
      this.cancelRefresh();
      this.swap(data.html);
    }

    onCartUpdate(event) {
      // After adding from this page, start again from 1 like Dawn does.
      if (event && event.source === 'product-form' && this.idInput) {
        if (String(event.productVariantId) === this.idInput.value) {
          this.wanted = 1;
          this.setQuantity(1);
        }
      }
      // The cart count and the stock left changed, so fetch fresh rows.
      this.refresh();
    }

    cancelRefresh() {
      clearTimeout(this.refreshTimer);
      if (this.refreshing) this.refreshing.abort();
      this.refreshing = null;
    }

    refresh() {
      this.cancelRefresh();
      const variantId = this.idInput && this.idInput.value;
      if (!variantId) return;
      const params = new URLSearchParams({ variant: variantId, section_id: this.dataset.section });
      const url = `${this.dataset.url}${this.dataset.url.includes('?') ? '&' : '?'}${params}`;

      this.refreshTimer = setTimeout(() => {
        const controller = 'AbortController' in window ? new AbortController() : null;
        this.refreshing = controller;
        fetch(url, controller ? { signal: controller.signal } : undefined)
          .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
          .then((text) => {
            if (this.refreshing !== controller) return;
            this.refreshing = null;
            this.swap(new DOMParser().parseFromString(text, 'text/html'));
          })
          .catch(() => {});
      }, 150);
    }

    swap(html) {
      const fresh = html.getElementById(this.id);
      // Only take rows for this product and for the variant the page's form will add: a quick add of
      // another product, or of this one with a different variant, renders the same section id.
      if (!fresh || fresh.dataset.productId !== this.dataset.productId) return;
      if (this.idInput && fresh.dataset.variantId !== this.idInput.value) return;

      ['variantId', 'price', 'cart', 'tiers', 'cap', 'nudge', 'top'].forEach((key) => {
        if (fresh.dataset[key] === undefined) delete this.dataset[key];
        else this.dataset[key] = fresh.dataset[key];
      });
      this.hidden = fresh.hidden;
      this.innerHTML = fresh.innerHTML;

      if (this.hidden) {
        if (this.wanted > 1) this.setQuantity(1);
        this.wanted = parseInt(this.input.value, 10) || 1;
        return;
      }
      this.wanted = this.allowed(this.wanted);
      this.setQuantity(this.wanted);
      this.render();
    }

    render() {
      if (this.hidden) return;
      const radios = Array.from(this.querySelectorAll('.etec-bulk__input'));
      if (!radios.length) return;

      const qty = this.wanted;
      const top = radios[radios.length - 1];
      const row =
        radios.find((radio) => parseInt(radio.value, 10) === qty) || (qty > parseInt(top.value, 10) ? top : null);
      radios.forEach((radio) => {
        radio.checked = radio === row && !radio.disabled;
      });

      const tiers = this.tiers;
      const count = (parseInt(this.dataset.cart, 10) || 0) + qty;
      const topQty = tiers.reduce((max, tier) => Math.max(max, tier[0]), 1);
      const pct = tiers.reduce((best, tier) => (tier[0] <= count && tier[1] > best ? tier[1] : best), 0);
      const next = tiers.filter((tier) => tier[0] > count && tier[1] > pct).sort((a, b) => a[0] - b[0])[0];
      const fill = (text, values) =>
        Object.keys(values).reduce((out, key) => out.split(`[${key}]`).join(String(values[key])), text || '');

      const fieldset = this.querySelector('.etec-bulk__fieldset');
      if (fieldset) {
        const share = topQty > 1 ? Math.min(1, (count - 1) / (topQty - 1)) : 1;
        fieldset.style.setProperty('--etec-bulk-fill', String(share));
      }
      this.querySelectorAll('.etec-bulk__notch, .etec-bulk__mark').forEach((mark) => {
        mark.classList.toggle('is-hit', parseInt(mark.dataset.qty, 10) <= count);
      });

      const nudge = this.querySelector('[data-etec-bulk-nudge]');
      if (nudge) {
        nudge.textContent = next
          ? fill(this.dataset.nudge, { count: next[0] - count, percent: next[1] })
          : fill(this.dataset.top, { percent: pct });
        nudge.classList.toggle('is-top', !next);
      }

      // No saving for more than can be bought: the add would fail.
      const save = this.querySelector('[data-etec-bulk-save]');
      const amount = this.querySelector('[data-etec-bulk-amount]');
      const status = this.querySelector('[data-etec-bulk-status]');
      const saving = qty > this.cap ? 0 : Math.round(((parseInt(this.dataset.price, 10) || 0) * qty * pct) / 100);
      const config = readConfig();
      const text = saving > 0 ? formatMoney(saving, config && config.moneyFormat) : '';
      if (save) save.classList.toggle('is-on', saving > 0);
      if (amount && text && amount.textContent !== text) {
        amount.textContent = text;
        // Restart the tick each time the amount changes.
        amount.classList.remove('is-bump');
        void amount.offsetWidth;
        amount.classList.add('is-bump');
      }
      // The visible line opens and closes; screen readers hear this one instead.
      if (status) {
        const message = save && text ? save.textContent.replace(/\s+/g, ' ').trim() : '';
        if (status.textContent !== message) status.textContent = message;
      }
    }
  }

  if (!customElements.get('etec-bulk-buy')) customElements.define('etec-bulk-buy', EtecBulkBuy);

  /* ---------- Category bar: all categories panel and dropdowns ------------------ */

  const canHover = window.matchMedia('(hover: hover)');
  const HOVER_OPEN_DELAY = 120;
  const HOVER_CLOSE_DELAY = 200;
  const PARENT_ITEM = '.etec-nav__item--parent';
  // Everything that opens on hover: the "All categories" button and each category with a dropdown.
  const HOVER_OWNER = `[data-etec-menu], ${PARENT_ITEM}`;

  let hoverItem = null;
  let openTimer = null;
  let closeTimer = null;
  let skipFocusOpen = false;

  // An owner is either the "All categories" wrapper or a category item; its trigger is the button or link.
  const menuTrigger = (owner) =>
    owner.matches('[data-etec-menu]')
      ? owner.querySelector('[data-etec-menu-toggle]')
      : owner.querySelector(':scope > .etec-nav__link');

  const menuPanel = (owner) =>
    owner.matches('[data-etec-menu]')
      ? owner.querySelector('[data-etec-menu-panel]')
      : owner.querySelector(':scope > .etec-nav__dropdown');

  const openOwner = () => document.querySelector(`[data-etec-menu].is-open, ${PARENT_ITEM}.is-open`);

  const markNavReady = () =>
    document.querySelectorAll('.etec-nav').forEach((nav) => nav.classList.add('etec-nav--js'));

  const closeMenus = (except) => {
    document.querySelectorAll(`[data-etec-menu].is-open, ${PARENT_ITEM}.is-open`).forEach((owner) => {
      if (owner === except) return;
      owner.classList.remove('is-open', 'is-instant');
      const trigger = menuTrigger(owner);
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  };

  // Keep a panel inside the bar and above anything fixed to the bottom of the window.
  const placePanel = (owner) => {
    const panel = menuPanel(owner);
    const inner = owner.closest('.etec-nav__inner');
    if (!panel || !inner) return;
    const bar = inner.getBoundingClientRect();
    const compareBar = document.querySelector('.etec-compare-bar:not([hidden])');
    const floor = compareBar ? compareBar.getBoundingClientRect().top : window.innerHeight;
    inner.style.setProperty('--etec-panel-max', `${Math.max(200, Math.round(floor - bar.bottom - 16))}px`);

    if (!owner.classList.contains('etec-nav__item--anchored')) return;
    panel.style.setProperty('--etec-panel-x', '0px');
    const style = window.getComputedStyle(inner);
    const left = bar.left + (parseFloat(style.paddingLeft) || 0);
    const right = bar.right - (parseFloat(style.paddingRight) || 0);
    const rect = panel.getBoundingClientRect();
    const shift = Math.max(Math.min(0, right - rect.right), left - rect.left);
    panel.style.setProperty('--etec-panel-x', `${Math.round(shift)}px`);
  };

  const openMenu = (owner) => {
    const switching = Boolean(openOwner());
    closeMenus(owner);
    if (owner.classList.contains('is-open')) return;
    placePanel(owner);
    owner.classList.toggle('is-instant', switching);
    owner.classList.add('is-open');
    const trigger = menuTrigger(owner);
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  };

  // Hover intent: a panel opens once the pointer has rested on its item (or on "All categories"), and
  // closes a moment after the pointer has left both the item and the panel. Crossing a neighbour on
  // the way to the open panel is too brief to switch.
  const setHoverItem = (item) => {
    if (item === hoverItem) return;
    hoverItem = item;
    window.clearTimeout(openTimer);
    window.clearTimeout(closeTimer);
    if (item) {
      if (item.classList.contains('is-open')) return;
      openTimer = window.setTimeout(() => openMenu(item), HOVER_OPEN_DELAY);
      return;
    }
    closeTimer = window.setTimeout(() => {
      document.querySelectorAll(`[data-etec-menu].is-open, ${PARENT_ITEM}.is-open`).forEach((open) => {
        if (!open.querySelector(':focus-visible')) closeMenus();
      });
    }, HOVER_CLOSE_DELAY);
  };

  document.addEventListener('mouseover', (event) => {
    if (!canHover.matches || !event.target.closest) return;
    setHoverItem(event.target.closest(HOVER_OWNER));
  });

  document.documentElement.addEventListener('mouseleave', () => {
    if (canHover.matches) setHoverItem(null);
  });

  document.addEventListener(
    'click',
    (event) => {
      // Close button in the mobile menu's top strip. Dawn binds every button in the drawer as a
      // "back" button, so this runs first and closes the whole drawer through Dawn's own method.
      const drawerClose = event.target.closest('[data-etec-drawer-close]');
      if (!drawerClose) return;
      const drawer = drawerClose.closest('header-drawer');
      if (!drawer || typeof drawer.closeMenuDrawer !== 'function') return;
      event.stopPropagation();
      const summary = drawer.querySelector('summary');
      drawer.closeMenuDrawer(event, summary);
      summary.setAttribute('aria-expanded', 'false');
    },
    true
  );

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-etec-menu-toggle]');
    if (toggle) {
      const menu = toggle.closest('[data-etec-menu]');
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
      // With a mouse the panel is already open from hovering, so a click keeps it open rather than
      // shutting it; moving away (or Escape) closes it. Touch and keyboard still toggle.
      const hoverClick = canHover.matches && event.detail > 0;
      if (menu.classList.contains('is-open') && !hoverClick) closeMenus();
      else openMenu(menu);
      return;
    }

    // Touch screens: the first tap on a parent link opens its dropdown, the second follows the link.
    const parentLink = event.target.closest(`${PARENT_ITEM} > .etec-nav__link`);
    if (parentLink && !canHover.matches) {
      const item = parentLink.parentElement;
      if (!item.classList.contains('is-open')) {
        event.preventDefault();
        openMenu(item);
        return;
      }
    }

    if (!event.target.closest(`[data-etec-menu], ${PARENT_ITEM}.is-open`)) closeMenus();
  });

  // Keyboard: tabbing onto a category opens its panel, tabbing out of the bar closes whatever is open.
  document.addEventListener('focusin', (event) => {
    if (skipFocusOpen || !event.target.matches || !event.target.matches(':focus-visible')) return;
    const item = event.target.closest(PARENT_ITEM);
    if (item) {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
      openMenu(item);
      return;
    }
    closeMenus(event.target.closest('[data-etec-menu]'));
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const owner = openOwner();
    if (!owner) return;
    window.clearTimeout(openTimer);
    window.clearTimeout(closeTimer);
    closeMenus();
    // Hand focus back to the trigger, unless the visitor is busy somewhere else on the page.
    const active = document.activeElement;
    const trigger = menuTrigger(owner);
    if (trigger && (!active || active === document.body || owner.contains(active))) {
      skipFocusOpen = true;
      trigger.focus();
      skipFocusOpen = false;
    }
  });

  markNavReady();
  document.addEventListener('shopify:section:load', markNavReady);

  // Long menus: keep the bar to one row. Links that don't fit are hidden here and stay
  // available in the "All categories" panel, so this only runs when that button exists.
  const fitNav = () => {
    document.querySelectorAll('.etec-nav__inner').forEach((inner) => {
      const list = inner.querySelector('.etec-nav__list');
      if (!list || !inner.querySelector('[data-etec-menu]')) return;
      const items = Array.from(list.children);
      if (!items.length) return;
      list.classList.add('etec-nav__list--single');
      items.forEach((item) => item.classList.remove('is-overflow'));
      // A link that wraps to a second row, or is squeezed past the bar's edge by the buttons, is hidden.
      const firstRow = items[0].offsetTop;
      const edge = list.getBoundingClientRect().right + 1;
      items.forEach((item) => {
        if (item.offsetTop > firstRow + 4 || item.getBoundingClientRect().right > edge) item.classList.add('is-overflow');
      });
    });
    // The bar may have just gone from two rows to one: let Dawn's sticky header re-measure, so
    // --header-height (used to offset sticky side panels) matches what is on screen.
    const stickyHeader = document.querySelector('sticky-header');
    if (stickyHeader && typeof stickyHeader.setHeaderHeight === 'function') stickyHeader.setHeaderHeight();
  };

  let navFrame = null;
  const queueFitNav = () => {
    if (navFrame) return;
    navFrame = window.requestAnimationFrame(() => {
      navFrame = null;
      fitNav();
    });
  };

  fitNav();
  window.addEventListener('resize', queueFitNav);
  window.addEventListener('load', queueFitNav);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(queueFitNav);
  document.addEventListener('shopify:section:load', queueFitNav);

  /* ---------- Scroll reveal and hero depth ------------------------------------- */

  // .etec-reveal items get .is-in as they come into view. Items in the same list follow each other,
  // and a whole list arrives together, so cards scrolled sideways on phones are never left hidden.
  // Hiding is only switched on (html.etec-reveal-on) by the head script in layout/theme.liquid.
  const root = document.documentElement;
  if (root.classList.contains('etec-reveal-on')) {
    const reveal = (item) => item.classList.add('is-in');
    const revealGroup = (item) => {
      const parent = item.parentElement;
      const group = parent ? Array.from(parent.children).filter((el) => el.classList.contains('etec-reveal')) : [item];
      group.forEach((el, i) => {
        el.style.setProperty('--etec-i', Math.min(i, 8));
        reveal(el);
        revealObserver.unobserve(el);
      });
    };
    const revealObserver =
      'IntersectionObserver' in window
        ? new IntersectionObserver(
            (entries) =>
              entries.forEach((entry) => {
                if (entry.isIntersecting) revealGroup(entry.target);
              }),
            { rootMargin: '0px 0px -8% 0px' }
          )
        : null;
    const watch = (scope) => {
      scope.querySelectorAll('.etec-reveal:not(.is-in)').forEach((item) => {
        if (revealObserver) revealObserver.observe(item);
        else reveal(item);
      });
    };
    watch(document);
    root.classList.add('etec-reveal-ready');
    // Product grids are re-rendered by filtering and "load more": watch what they add.
    document.addEventListener('shopify:section:load', (event) => watch(event.target));
    if ('MutationObserver' in window && document.getElementById('MainContent')) {
      new MutationObserver(() => watch(document.getElementById('MainContent'))).observe(
        document.getElementById('MainContent'),
        { childList: true, subtree: true }
      );
    }
  }

  // Hero: --etec-par follows the scroll position while the hero is on screen; the CSS turns it into a
  // slow drift of the product and the grid. Nothing runs once the hero has scrolled away.
  const heroes = Array.from(document.querySelectorAll('.etec-hero__slider'));
  if (heroes.length && !reduceMotion.matches && 'IntersectionObserver' in window) {
    const visible = new Set();
    let parFrame = null;
    const paint = () => {
      parFrame = null;
      visible.forEach((hero) => {
        // Pixels scrolled since the hero was at its resting place on screen (0 at the top of the page).
        const rect = hero.getBoundingClientRect();
        const rest = Math.min(rect.top + window.scrollY, window.innerHeight);
        hero.style.setProperty('--etec-par', Math.max(0, Math.round(rest - rect.top)));
      });
    };
    const queuePaint = () => {
      if (!parFrame && visible.size) parFrame = window.requestAnimationFrame(paint);
    };
    const heroObserver = new IntersectionObserver((entries) =>
      entries.forEach((entry) => {
        if (entry.isIntersecting) visible.add(entry.target);
        else visible.delete(entry.target);
        queuePaint();
      })
    );
    heroes.forEach((hero) => heroObserver.observe(hero));
    window.addEventListener('scroll', queuePaint, { passive: true });
  }

  /* ---------- All categories: category rail and panes ------------------------------ */

  // Pointing at (or tabbing to) a category in the rail shows its menu on the right. A short pause
  // before switching lets the pointer cross other categories on its way into the pane.
  const showCategory = (tab) => {
    const cats = tab.closest('[data-etec-allcats]');
    const pane = cats && document.getElementById(tab.dataset.etecCat);
    if (!pane || pane.classList.contains('is-active')) return;
    cats.querySelectorAll('.etec-allcats__tab.is-active').forEach((el) => el.classList.remove('is-active'));
    cats.querySelectorAll('[data-etec-cat-pane].is-active').forEach((el) => el.classList.remove('is-active'));
    tab.classList.add('is-active');
    pane.classList.add('is-active');
    pane.parentElement.scrollTop = 0;
  };
  let catTimer = null;
  document.addEventListener(
    'pointerover',
    (event) => {
      const tab = event.target.closest && event.target.closest('[data-etec-cat]');
      if (!tab) return;
      window.clearTimeout(catTimer);
      catTimer = window.setTimeout(() => showCategory(tab), event.pointerType === 'mouse' ? 70 : 0);
    },
    { passive: true }
  );
  document.addEventListener('pointerout', (event) => {
    if (event.target.closest && event.target.closest('[data-etec-cat]')) window.clearTimeout(catTimer);
  });
  document.addEventListener('focusin', (event) => {
    const tab = event.target.closest && event.target.closest('[data-etec-cat]');
    if (tab) showCategory(tab);
  });

  /* ---------- Decoding labels ---------------------------------------------------- */

  // Text that settles out of random characters, left to right. Footer headings ([data-etec-decode])
  // decode when they first come into view and again on hover; menu labels ([data-etec-decode="hover"])
  // decode on hover or keyboard focus only. The real text stays in the element for screen readers;
  // only an aria-hidden copy is scrambled, and its width is locked first so nothing beside it moves.
  // Nothing is set up for visitors who prefer reduced motion, on touch-only screens (hover labels),
  // or with the theme's decorative motion off.
  const decodeGlyphs = 'ABCDEFGHJKLMNPRSTUVWXYZ0123456789/#+';
  const decodeAllowed = () => !reduceMotion.matches && document.body.classList.contains('etec-motion');

  // Splits a plain-text element into a hidden real copy and a visible face; returns false if it can't.
  const prepareDecode = (el) => {
    if (el.dataset.etecText) return true;
    const text = el.textContent.trim();
    if (!text || el.children.length) return false;
    el.dataset.etecText = text;
    const real = document.createElement('span');
    real.className = 'visually-hidden';
    real.textContent = text;
    const face = document.createElement('span');
    face.className = 'etec-decode__face';
    face.setAttribute('aria-hidden', 'true');
    face.textContent = text;
    el.replaceChildren(real, face);
    return true;
  };

  const decode = (el, base = 380, perChar = 22) => {
    const face = el.querySelector('.etec-decode__face');
    const text = el.dataset.etecText;
    const now = performance.now();
    if (!face || !text || el.etecDecoding || now - (el.etecDecodedAt || 0) < 400) return;
    el.etecDecoding = true;
    // Lock the width once, so changing glyph widths never move the items around it.
    if (!face.style.width) face.style.width = `${face.getBoundingClientRect().width}px`;
    const duration = base + text.length * perChar;
    const frame = (time) => {
      const progress = Math.min((time - now) / duration, 1);
      const locked = Math.floor(progress * text.length);
      let out = text.slice(0, locked);
      for (let i = locked; i < text.length; i += 1) {
        out += text[i] === ' ' ? ' ' : decodeGlyphs[(Math.random() * decodeGlyphs.length) | 0];
      }
      face.textContent = out;
      if (progress < 1) {
        window.requestAnimationFrame(frame);
      } else {
        el.etecDecoding = false;
        el.etecDecodedAt = performance.now();
      }
    };
    window.requestAnimationFrame(frame);
  };

  // Footer headings: decode once on the way into view, and again on hover.
  const setUpDecode = (scope) => {
    if (!decodeAllowed()) return;
    const headings = Array.from(scope.querySelectorAll('[data-etec-decode]:not([data-etec-decode="hover"])')).filter(
      (heading) => !heading.dataset.etecText && prepareDecode(heading)
    );
    if (!headings.length) return;
    const decodeObserver =
      'IntersectionObserver' in window
        ? new IntersectionObserver(
            (entries) =>
              entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                decodeObserver.unobserve(entry.target);
                decode(entry.target);
              }),
            { rootMargin: '0px 0px -10% 0px' }
          )
        : null;
    headings.forEach((heading) => {
      heading.addEventListener('mouseenter', () => decode(heading));
      if (decodeObserver) decodeObserver.observe(heading);
    });
  };
  setUpDecode(document);
  document.addEventListener('shopify:section:load', (event) => setUpDecode(event.target));

  // Menu labels: one delegated listener per event type for the whole page. Each label is prepared
  // the first time the pointer or focus reaches it, so page load does no work for them at all.
  const hoverCapable = window.matchMedia('(hover: hover)');
  const decodeTargetFrom = (event) => {
    const host = event.target.closest && event.target.closest('a, button, [data-etec-decode="hover"]');
    if (!host) return null;
    if (host.matches('[data-etec-decode="hover"]')) return host;
    return host.querySelector('[data-etec-decode="hover"]');
  };
  const decodeOnPointer = (event) => {
    if (!decodeAllowed() || !hoverCapable.matches) return;
    const target = decodeTargetFrom(event);
    // Moving between the parts of one link (icon, label) is not a new hover.
    const host = target && (target.closest('a, button') || target);
    if (host && event.relatedTarget && host.contains(event.relatedTarget)) return;
    if (target && (target.dataset.etecText || prepareDecode(target))) decode(target, 220, 12);
  };
  const decodeOnFocus = (event) => {
    if (!decodeAllowed() || !event.target.matches(':focus-visible')) return;
    const target = decodeTargetFrom(event);
    if (target && (target.dataset.etecText || prepareDecode(target))) decode(target, 220, 12);
  };
  document.addEventListener('pointerover', decodeOnPointer, { passive: true });

  // Stock counter: pointing at it rolls the digits again (at most every 1.5s). The CSS keeps two copies of
  // the roll keyframes; swapping between them (.is-alt) restarts the animation without reading layout.
  document.addEventListener(
    'pointerover',
    (event) => {
      if (!decodeAllowed() || !hoverCapable.matches) return;
      const counter = event.target.closest && event.target.closest('[data-etec-stockcount]');
      if (!counter || (event.relatedTarget && counter.contains(event.relatedTarget))) return;
      const now = performance.now();
      if (now - (counter.etecRolledAt || 0) < 1500) return;
      counter.etecRolledAt = now;
      counter.classList.toggle('is-alt');
    },
    { passive: true }
  );
  document.addEventListener('focusin', decodeOnFocus);

  /* ---------- Newest-products tiles --------------------------------------- */

  // A "newest products" hero tile can only see the first 250 products in Liquid, so a big import of
  // products without photos leaves it with stand-ins. This walks the collection's JSON feed
  // (templates/collection.etec-thumbs.liquid, newest first) until it finds enough products that do
  // have photos, and shows those. The last result is kept in localStorage so repeat visits show
  // photos straight away, and newer arrivals with photos replace them as soon as they exist.
  // Large imports without photos can push the last photos thousands of products back, so pages are
  // fetched a few at a time, the whole feed at most, and the result is refreshed at most hourly.
  const ARRIVALS_PAGES = 40;
  const ARRIVALS_BATCH = 6;
  const ARRIVALS_CACHE_DAYS = 30;
  const ARRIVALS_REFRESH_MS = 36e5;

  const arrivalsCacheKey = (handle) => `etec:arrivals:${handle}`;

  const readArrivalsCache = (handle) => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(arrivalsCacheKey(handle)) || 'null');
      if (!saved || !Array.isArray(saved.items)) return null;
      if (Date.now() - saved.at > ARRIVALS_CACHE_DAYS * 864e5) return null;
      return saved;
    } catch (error) {
      return null;
    }
  };

  const writeArrivalsCache = (handle, items) => {
    try {
      window.localStorage.setItem(arrivalsCacheKey(handle), JSON.stringify({ at: Date.now(), items }));
    } catch (error) {
      /* Storage blocked or full: the tile still works, it just fetches again next time. */
    }
  };

  const arrivalsThumb = (item) => {
    const thumb = document.createElement('span');
    thumb.className = 'etec-thumb is-arriving';
    thumb.dataset.etecPick = item.key;
    const img = document.createElement('img');
    img.src = item.w320;
    img.srcset = `${item.w120} 120w, ${item.w200} 200w, ${item.w320} 320w`;
    img.sizes = '(min-width: 750px) 120px, 30vw';
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.width = 320;
    img.height = 320;
    thumb.append(img);
    return thumb;
  };

  const sameArrivals = (box, items) => {
    const shown = Array.from(box.children).map((el) => el.dataset.etecPick || '');
    return shown.length === items.length && items.every((item, i) => item.key === shown[i]);
  };

  const showArrivals = (box, items) => {
    if (!items.length || sameArrivals(box, items)) return;
    // Keep any stand-ins we have no photo for, so the row stays full.
    const keys = new Set(items.map((item) => item.key));
    const keep = Array.from(box.children)
      .slice(items.length)
      .filter((el) => !keys.has(el.dataset.etecPick));
    box.replaceChildren(...items.map(arrivalsThumb), ...keep);
    requestAnimationFrame(() =>
      box.querySelectorAll('.is-arriving').forEach((el) => el.classList.remove('is-arriving'))
    );
  };

  const loadArrivals = async (box) => {
    const handle = box.dataset.etecArrivals;
    const want = parseInt(box.dataset.count, 10) || 3;
    if (!handle || box.etecArrivalsLoading) return;
    box.etecArrivalsLoading = true;

    const used = new Set(
      Array.from(document.querySelectorAll('[data-etec-pick]'))
        .filter((el) => !box.contains(el))
        .map((el) => el.dataset.etecPick)
        .filter(Boolean)
    );
    const pickable = (item) => item && item.key && !used.has(item.key);

    const saved = readArrivalsCache(handle);
    const cached = saved ? saved.items.filter(pickable).slice(0, want) : [];
    if (cached.length) showArrivals(box, cached);
    if (cached.length >= want && Date.now() - saved.at < ARRIVALS_REFRESH_MS) {
      box.etecArrivalsLoading = false;
      return;
    }

    const base = `${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}collections/${encodeURIComponent(handle)}`;
    const fetchPage = (page) =>
      fetch(`${base}?view=etec-thumbs&sort_by=created-descending&page=${page}`, { credentials: 'same-origin' })
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null);

    // Pages come back in batches but are read in order, so the newest photos always win.
    const found = [];
    const seen = new Set();
    let pages = ARRIVALS_PAGES;
    for (let start = 1; start <= pages && found.length < want; start += ARRIVALS_BATCH) {
      const numbers = [];
      for (let page = start; page < start + ARRIVALS_BATCH && page <= pages; page += 1) numbers.push(page);
      const feeds = await Promise.all(numbers.map(fetchPage));
      if (feeds.every((feed) => !feed)) break;
      feeds.forEach((feed) => {
        if (!feed) return;
        if (feed.pages) pages = Math.min(pages, feed.pages);
        (feed.items || []).forEach((item) => {
          if (found.length >= want || !pickable(item) || seen.has(item.key)) return;
          seen.add(item.key);
          found.push(item);
        });
      });
    }
    box.etecArrivalsLoading = false;
    if (!found.length) return;
    writeArrivalsCache(handle, found);
    showArrivals(box, found);
  };

  const setUpArrivals = (root) => {
    root.querySelectorAll('[data-etec-arrivals]').forEach((box) => loadArrivals(box));
  };
  setUpArrivals(document);
  document.addEventListener('shopify:section:load', (event) => setUpArrivals(event.target));

  /* ---------- Short product rows ------------------------------------------ */

  // A product row (snippets/etec-product-grid.liquid) can only look at the first 250 products in
  // Liquid, and big imports without photos can leave it with only a few cards. When the row is
  // close to view, this walks the collection's card feed (templates/collection.etec-cards.liquid)
  // in the row's order until the row is full. Rows in hidden tabs wait until their tab opens.
  const MORE_PAGES = 40;
  const MORE_BATCH = 4;
  const collectionsBase = () =>
    `${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}collections/`;

  const fillRow = async (list) => {
    if (list.etecMoreLoading || list.etecMoreDone) return;
    list.etecMoreLoading = true;
    const handle = list.dataset.etecMore;
    const want = parseInt(list.dataset.etecMoreCount, 10) || 5;
    const sort = list.dataset.etecMoreSort ? `&sort_by=${encodeURIComponent(list.dataset.etecMoreSort)}` : '';
    const rowId = list.dataset.etecMoreId || 'etec-more';
    const shown = new Set(Array.from(list.children).map((el) => el.dataset.etecProduct));
    const base = `${collectionsBase()}${encodeURIComponent(handle)}?view=etec-cards${sort}`;
    const fetchPage = (page) =>
      fetch(`${base}&page=${page}`, { credentials: 'same-origin' })
        .then((response) => (response.ok ? response.text() : null))
        .then((html) => {
          if (!html) return null;
          const template = document.createElement('template');
          template.innerHTML = html.replace(/etecmorecards/g, rowId);
          return template.content.querySelector('[data-etec-page]');
        })
        .catch(() => null);

    // Pages come back in batches but are added in order, so the row keeps the feed's order.
    let pages = MORE_PAGES;
    for (let start = 1; start <= pages && list.children.length < want; start += MORE_BATCH) {
      const numbers = [];
      for (let page = start; page < start + MORE_BATCH && page <= pages; page += 1) numbers.push(page);
      const feeds = await Promise.all(numbers.map(fetchPage));
      if (feeds.every((feed) => !feed)) break;
      feeds.forEach((feed) => {
        if (!feed) return;
        const total = parseInt(feed.dataset.etecPages, 10);
        if (total) pages = Math.min(pages, total);
        Array.from(feed.children).forEach((card) => {
          if (list.children.length >= want || shown.has(card.dataset.etecProduct)) return;
          shown.add(card.dataset.etecProduct);
          card.classList.add('is-in');
          list.append(card);
        });
      });
    }
    list.etecMoreLoading = false;
    list.etecMoreDone = true;
    // The shelf's arrows only watch the track's size, so tell it more cards arrived.
    const shelf = list.closest('etec-shelf');
    if (shelf && shelf.queueUpdate) shelf.queueUpdate();
  };

  const rowObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) =>
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              rowObserver.unobserve(entry.target);
              fillRow(entry.target);
            }),
          { rootMargin: '600px 0px' }
        )
      : null;

  const setUpRows = (scope) => {
    scope.querySelectorAll('[data-etec-more]').forEach((list) => {
      if (rowObserver) rowObserver.observe(list);
      else fillRow(list);
    });
  };
  setUpRows(document);
  document.addEventListener('shopify:section:load', (event) => setUpRows(event.target));

  /* ---------- Category tiles without a photo -------------------------------- */

  // A category tile whose first 250 products have no photo keeps its icon in Liquid. This looks
  // further back with the photo feed (templates/collection.etec-thumbs.liquid) and shows the first
  // photo it finds. The last photo found is kept in localStorage, so repeat visits show it
  // straight away while the feed is checked again at most hourly.
  const CAT_PAGES = 40;
  const CAT_BATCH = 6;
  const catCacheKey = (handle) => `etec:catphoto:${handle}`;

  const showCatPhoto = (art, item) => {
    const img = document.createElement('img');
    img.className = 'etec-cat__image';
    img.src = item.w320;
    img.srcset = `${item.w120} 120w, ${item.w200} 200w, ${item.w320} 320w`;
    img.sizes = '(min-width: 1200px) 180px, (min-width: 750px) 22vw, 30vw';
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';
    art.replaceChildren(img);
    const tile = art.closest('.etec-cat');
    if (tile) tile.classList.add('etec-cat--photo');
  };

  const loadCatPhoto = async (art) => {
    const handle = art.dataset.etecCatFeed;
    if (!handle || art.etecCatLoading) return;
    art.etecCatLoading = true;

    let saved = null;
    try {
      saved = JSON.parse(window.localStorage.getItem(catCacheKey(handle)) || 'null');
    } catch (error) {
      saved = null;
    }
    if (saved && saved.item) showCatPhoto(art, saved.item);
    if (saved && saved.item && Date.now() - saved.at < ARRIVALS_REFRESH_MS) return;

    const base = `${collectionsBase()}${encodeURIComponent(handle)}?view=etec-thumbs`;
    const fetchPage = (page) =>
      fetch(`${base}&page=${page}`, { credentials: 'same-origin' })
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null);

    let found = null;
    let pages = CAT_PAGES;
    for (let start = 1; start <= pages && !found; start += CAT_BATCH) {
      const numbers = [];
      for (let page = start; page < start + CAT_BATCH && page <= pages; page += 1) numbers.push(page);
      const feeds = await Promise.all(numbers.map(fetchPage));
      if (feeds.every((feed) => !feed)) break;
      feeds.forEach((feed) => {
        if (!feed || found) return;
        if (feed.pages) pages = Math.min(pages, feed.pages);
        found = (feed.items || [])[0] || null;
      });
    }
    if (!found) return;
    try {
      window.localStorage.setItem(catCacheKey(handle), JSON.stringify({ at: Date.now(), item: found }));
    } catch (error) {
      /* Storage blocked or full: the tile still works, it just fetches again next time. */
    }
    if (!saved || !saved.item || saved.item.key !== found.key) showCatPhoto(art, found);
  };

  const setUpCatPhotos = (scope) => {
    scope.querySelectorAll('[data-etec-cat-feed]').forEach((art) => loadCatPhoto(art));
  };
  setUpCatPhotos(document);
  document.addEventListener('shopify:section:load', (event) => setUpCatPhotos(event.target));

  // Theme editor: re-run nothing special here, custom elements re-upgrade themselves when
  // a section reloads. Keep menus closed after a reload.
  if (inEditor()) document.addEventListener('shopify:section:load', () => closeMenus());
})();
